import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { useCreatorContext, useCreatorAction } from "../ui";
import { useT } from "../sprache";
import { GUARDIAN_STATEMENT_VERSION } from "../actions";

/** Stand der Einwilligung (public.talent_guardian_status, 0093) */
export interface GuardianStatus { minor: boolean; consent: boolean; guardian_name: string | null; guardian_email: string | null }

/** Fehlt die Einwilligung? Nur heute minderjährig und ohne Einwilligung (CREATOR-R3-2). */
export function einwilligungFehlt(s: GuardianStatus | null | undefined): boolean {
  return !!s && s.minor && !s.consent;
}

/**
 * Einwilligung der Eltern nachreichen (CREATOR-R3-2): Wird das Geburtsdatum erst nach dem Annehmen der Einladung eingetragen,
 * hat niemand die Einwilligung abgefragt. Das Portal holt sie hier nach; die Eltern bekommen einen Hinweis per Mail, Verträge
 * unterschreiben sie weiter selbst über ihren Link.
 */
function useEinwilligung() {
  const { ws, scope } = useCreatorContext();
  return useQuery({
    queryKey: ["creator", scope.talentId, "einwilligung"],
    queryFn: () => api.read<GuardianStatus>("talent_guardian_status", { p_talent: scope.talentId }),
    enabled: !ws.archived,
  });
}

/**
 * Prüf-Chat CP-005 (02.11.2026): Die fehlende Einwilligung stand nur unter „Verträge“. Die Übersicht nennt sie jetzt als
 * nächsten Schritt mit Sprung zum Formular.
 */
export function ElternEinwilligungHinweis() {
  const t = useT();
  const q = useEinwilligung();
  if (!einwilligungFehlt(q.data)) return null;
  return (
    <div className="notice stack tk-gap-sm" role="status">
      <p><strong>{t("Einwilligung deiner Erziehungsberechtigten fehlt.", "Consent of your parent or guardian is missing.")}</strong> {t("Laut deinem Profil bist du unter 18. Bitte füllt die Einwilligung gemeinsam aus; erst dann kann deine Agentur dir Verträge schicken.",
        "According to your profile you are under 18. Please fill in the consent together; only then can your agency send you contracts.")}</p>
      <div className="row"><Link className="btn primary" to="/creator/vertraege#einwilligung">{t("Einwilligung ausfüllen", "Fill in consent")}</Link></div>
    </div>
  );
}

export function ElternEinwilligung() {
  const { ws, scope } = useCreatorContext();
  const t = useT();
  const q = useEinwilligung();
  const [value, setValue] = useState({ name: "", email: "", confirmed: false });
  const [problem, setProblem] = useState<string | null>(null);
  // Prüf-Chat CP-005 (02.11.2026): nach dem Speichern verschwand der Kasten ohne sichtbare Bestätigung; jetzt bleibt sie stehen
  const [gespeichert, setGespeichert] = useState<{ name: string; email: string } | null>(null);
  const { busy, error, attempt } = useCreatorAction();
  if (gespeichert) {
    return (
      <div className="notice" role="status" id="einwilligung">
        {t(`Gespeichert. Die Einwilligung von ${gespeichert.name} liegt jetzt bei ${ws.org.name}; an ${gespeichert.email} geht ein Hinweis per E-Mail.`,
          `Saved. The consent of ${gespeichert.name} is now with ${ws.org.name}; a notice is sent by email to ${gespeichert.email}.`)}
      </div>
    );
  }
  if (!einwilligungFehlt(q.data)) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const p = value.name.trim().length < 2 ? t("Bitte den Namen deiner erziehungsberechtigten Person angeben.", "Please enter the name of your parent or guardian.")
      : !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.email.trim()) ? t("Bitte eine gültige E-Mail-Adresse deiner erziehungsberechtigten Person angeben.", "Please enter a valid email address of your parent or guardian.")
        : !value.confirmed ? t("Deine erziehungsberechtigte Person muss die Erklärung bestätigen.", "Your parent or guardian must confirm the statement.") : null;
    setProblem(p);
    if (p) return;
    const ok = await attempt("einwilligung", () => api.rpc("talent_guardian_consent", {
      p_talent: scope.talentId, p_guardian_name: value.name.trim(), p_guardian_email: value.email.trim(), p_version: GUARDIAN_STATEMENT_VERSION,
    }), t("Danke, die Einwilligung ist gespeichert.", "Thank you, the consent has been saved."));
    if (ok) setGespeichert({ name: value.name.trim(), email: value.email.trim() });
  }

  return (
    <form className="panel stack" id="einwilligung" onSubmit={(e) => void submit(e)} noValidate aria-busy={!!busy} aria-label={t("Einwilligung deiner Erziehungsberechtigten", "Consent of your parent or guardian")}>
      <h2>{t("Einwilligung deiner Erziehungsberechtigten", "Consent of your parent or guardian")}</h2>
      <p>{t(`Laut deinem Profil bist du unter 18. Für die Zusammenarbeit mit ${ws.org.name} braucht es die Einwilligung deiner Mutter, deines Vaters oder einer anderen erziehungsberechtigten Person. Bitte füllt das gemeinsam aus.`,
        `According to your profile you are under 18. Working with ${ws.org.name} requires the consent of your mother, father or another parent or guardian. Please fill this in together.`)}</p>
      <label>{t("Name der erziehungsberechtigten Person", "Name of parent or guardian")}<input value={value.name} onChange={(e) => setValue({ ...value, name: e.target.value })} autoComplete="off" /></label>
      <label>{t("Ihre E-Mail-Adresse", "Their email address")}<input type="email" value={value.email} onChange={(e) => setValue({ ...value, email: e.target.value })} autoComplete="off" />
        <small>{t(`Dorthin schicken wir einen Hinweis auf diese Einwilligung; Verträge schickt ${ws.org.name} dorthin zur Unterschrift.`, `We send a notice of this consent to this address; ${ws.org.name} sends contracts there for signature.`)}</small></label>
      <label className="row tk-check">
        <input type="checkbox" checked={value.confirmed} onChange={(e) => setValue({ ...value, confirmed: e.target.checked })} />
        <span>{t(`Ich bin erziehungsberechtigt und bin einverstanden, dass mein Kind mit ${ws.org.name} zusammenarbeitet. Verträge unterschreibe ich über meinen eigenen Link.`,
          `I am the parent or guardian and agree that my child works with ${ws.org.name}. I sign contracts via my own link.`)}</span>
      </label>
      {(problem ?? error) && <p className="tk-error" role="alert">{problem ?? error}</p>}
      <button className="primary" type="submit" disabled={!!busy}>{busy === "einwilligung" ? t("Wird gespeichert …", "Saving …") : t("Einwilligung speichern", "Save consent")}</button>
    </form>
  );
}
